<ng-template
  #expandable
  let-id="id"
  let-columnId="columnId"
  let-all="all"
  let-allLength="allLength"
  let-short="short"
  let-shortLength="shortLength"
  let-template="template"
  let-expandLabel="expandLabel"
  let-collapseLabel="collapseLabel"
  let-group="group"
>
  @if (shortLength > 0) {
    <ng-container
      [ngTemplateOutlet]="template"
      [ngTemplateOutletContext]="{
        $implicit: expandedListItems[columnId][id] ? all : short,
      }"
    />
    @if (allLength > shortLength) {
      <rt-button
        variant="outline"
        margin-tb-small
        (click)="
          $event.stopPropagation();
          expandedListItems[columnId][id] ? collapseList(group, id) : expandList(group, id)
        "
      >
        <rt-truncate>
          @if (expandedListItems[columnId][id]) {
            {{ collapseLabel }}
          }
          @if (!expandedListItems[columnId][id]) {
            {{ expandLabel }}
          }
        </rt-truncate>
      </rt-button>
    }
  }
  @if (shortLength === 0) {
    <rt-truncate>
      <ng-container [ngTemplateOutlet]="emptyCell" />
    </rt-truncate>
  }
</ng-template>

<rt-data-table
  [rowsToPaintAtStart]="rowsToPaintAtStart()"
  [clickable]="true"
  [hoverable]="true"
  [selectable]="selectable()"
  [cellPadding]="false"
  [raised]="true"
  [autoFit]="autoFit()"
  [cols]="columns()"
  [rows]="requisitionsRows"
  [defaultCellTemplate]="defaultCell"
  [cellTemplates]="cellTemplates"
  [selectedIdsDict]="selectedIds()"
  (rowClick)="openRequisition.emit($event.id)"
  (rowSelect)="rowSelect.emit($event)"
  (sortChange)="sortChange.emit($event)"
  (geometryChange)="geometryChange.emit($event)"
  text-darkest
/>

<ng-template
  #defaultCell
  let-requisition="row"
  let-columnId="columnId"
  [ngTemplateType]="RequisitionTableCellContext"
>
  @if ($any(requisition)[toCamelCase(columnId.toString())]; as DefaultValue) {
    <div text-align>
      {{ DefaultValue }}
    </div>
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #title let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <div class="title">
    <a (click)="openRequisition.emit(requisition.id)">
      <span>{{ requisition.title }}</span>
    </a>
  </div>
</ng-template>

<ng-template #status let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <rt-requisitions-status-badge
    [status]="requisition.status"
    [isArchived]="!!requisition.archivedAt"
  />
</ng-template>

<ng-template #department let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.departmentName; as Department) {
    {{ Department }}
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #dateCreated let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <rt-date [time]="requisition.createdAt" [tooltipPosition]="'left'" />
</ng-template>

<ng-template #location let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (locationPerRequisitionMap[requisition.id]; as Location) {
    {{ Location }}
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #jobs let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <ng-container
    [ngTemplateOutlet]="expandable"
    [ngTemplateOutletContext]="{
      group: 'offers',
      id: requisition.id,
      columnId: 'jobs',
      all: requisition.offers,
      allLength: requisition.offers.length,
      short: requisition.visibleOffers || [],
      shortLength: (requisition.visibleOffers || []).length,
      template: jobsList,
      expandLabel:
        'app.requisitions.list.table.cell_expand.expand_jobs'
        | translate
          : { count: requisition.offers.length - (requisition.visibleOffers || []).length },
      collapseLabel: 'app.requisitions.list.table.cell_expand.hide' | translate,
    }"
  />
</ng-template>

<ng-template #jobsList let-offers>
  @if (offers.length) {
    @for (job of offers; track trackOfferById($index, job)) {
      <div
        class="job"
        padding-tb-small-x
        [routerLink]="hasAccessToJob(job.id) ? '/offers/' + job.slug + '/pipeline' : null"
        [class.not-clickable]="!hasAccessToJob(job.id)"
      >
        <rt-offer-status-indicator [offerStatus]="job" margin-right-small-xx />
        <span class="job-title">{{ job.title }}</span>
      </div>
    }
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #followers let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.followerIds?.length) {
    <rt-admins-avatars [admins]="followersPerRequisitionMap[requisition.id]" />
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #hires let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  {{ requisition.hiresCount }}/{{ requisition.openingsCount }}
</ng-template>

<ng-template #recruiter let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.recruiterId) {
    @if (this.usersMap()[requisition.recruiterId]; as Recruiter) {
      <ng-container [ngTemplateOutlet]="userTpl" [ngTemplateOutletContext]="{ user: Recruiter }" />
    } @else {
      <ng-container [ngTemplateOutlet]="deletedUser" />
    }
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #hiringManager let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.hiringManagerId) {
    @if (this.usersMap()[requisition.hiringManagerId]; as HiringManager) {
      <ng-container
        [ngTemplateOutlet]="userTpl"
        [ngTemplateOutletContext]="{ user: HiringManager }"
      />
    } @else {
      <ng-container [ngTemplateOutlet]="deletedUser" />
    }
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #requester let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.approvalRequest?.requesterId) {
    @if (this.usersMap()[requisition.approvalRequest?.requesterId!]; as Requester) {
      <ng-container [ngTemplateOutlet]="userTpl" [ngTemplateOutletContext]="{ user: Requester }" />
    } @else {
      <ng-container [ngTemplateOutlet]="deletedUser" />
    }
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #reasonForOpening let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (reasonsForOpeningPerRequisitionMap[requisition.id]; as Reason) {
    {{ Reason }}
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #priority let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  @if (requisition.priority; as Priority) {
    <div layout layout-align-items-center>
      <rt-icon
        [name]="priorityDataMap[Priority].icon!"
        [colorToken]="priorityDataMap[Priority].iconColorToken"
        margin-right-small-x
      />
      <span>{{ priorityDataMap[Priority].label }}</span>
    </div>
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #locationName let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <ng-container
    [ngTemplateOutlet]="expandable"
    [ngTemplateOutletContext]="{
      group: 'locations',
      id: requisition.id,
      columnId: 'location_name',
      all: requisition.locations || [],
      allLength: (requisition.locations || []).length,
      short: requisition.visibleLocations || [],
      shortLength: (requisition.visibleLocations || []).length,
      template: locationNameList,
      expandLabel:
        'app.requisitions.list.table.cell_expand.expand_jobs'
        | translate
          : {
              count:
                (requisition.locations || []).length - (requisition.visibleLocations || []).length,
            },
      collapseLabel: 'app.requisitions.list.table.cell_expand.hide' | translate,
    }"
  />
</ng-template>

<ng-template #locationNameList let-locations>
  @for (location of locations; track trackLocationById($index, location)) {
    <rt-truncate padding-tb-small-x>
      {{ location.name }}
    </rt-truncate>
  }
</ng-template>

<ng-template #locationAddress let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <ng-container
    [ngTemplateOutlet]="expandable"
    [ngTemplateOutletContext]="{
      group: 'locations',
      id: requisition.id,
      columnId: 'location_address',
      all: requisition.locations || [],
      allLength: (requisition.locations || []).length,
      short: requisition.visibleLocations || [],
      shortLength: (requisition.visibleLocations || []).length,
      template: locationAddressList,
      expandLabel:
        'app.requisitions.list.table.cell_expand.expand_jobs'
        | translate
          : {
              count:
                (requisition.locations || []).length - (requisition.visibleLocations || []).length,
            },
      collapseLabel: 'app.requisitions.list.table.cell_expand.hide' | translate,
    }"
  />
</ng-template>

<ng-template #locationAddressList let-locations>
  @for (location of locations; track trackLocationById($index, location)) {
    <rt-truncate padding-tb-small-x>
      {{ location.fullAddress }}
    </rt-truncate>
  }
</ng-template>

<ng-template
  #workModel
  let-requisition="row"
  let-columnId="columnId"
  [ngTemplateType]="RequisitionTableCellContext"
>
  @if (requisition.remote || requisition.hybrid || requisition.onSite) {
    <div>
      @if (requisition.onSite) {
        <span>{{ 'app.offer_form.work_model_section.on_site.title' | translate }}</span>
      }
      @if (requisition.hybrid) {
        @if (requisition.onSite) {
          {{ ', ' }}
        }
        <span>{{ 'app.offer_form.work_model_section.hybrid.title' | translate }}</span>
      }
      @if (requisition.remote) {
        @if (requisition.onSite || requisition.hybrid) {
          {{ ', ' }}
        }
        <span>{{ 'app.offer_form.work_model_section.remote.title' | translate }}</span>
      }
    </div>
  } @else {
    <ng-container [ngTemplateOutlet]="emptyCell" />
  }
</ng-template>

<ng-template #manage let-requisition="row" [ngTemplateType]="RequisitionTableCellContext">
  <rt-requisition-table-row-actions
    [requisition]="requisition"
    [canEditRequisition]="canEditRequisitions()"
    (click)="$event.stopImmediatePropagation()"
    (follow)="follow.emit($event)"
    (unfollow)="unfollow.emit($event)"
    (duplicate)="duplicate.emit($event)"
    (archive)="archive.emit($event)"
    (retrieve)="retrieve.emit($event)"
    (delete)="delete.emit($event)"
    (cancelRequest)="cancelRequest.emit($event)"
    (openRequisition)="openRequisition.emit($event)"
  />
</ng-template>

<ng-template #userTpl let-user="user">
  <div layout layout-align-items-center>
    <rt-avatar
      [type]="user.hasAvatar ? 'image' : 'initials'"
      size="small"
      [imageSrc]="user.photoThumbUrl"
      [initials]="user.initials"
      margin-right-small
      context="admin"
    />
    {{ user.firstName }} {{ user.lastName }}
  </div>
</ng-template>

<ng-template #deletedUser>
  {{ 'app.common.deleted_user' | translate }}
</ng-template>

<ng-template #emptyCell>
  <span class="empty">—</span>
</ng-template>
